import { type TableColumnView } from "@finway-group/shared/lib/models/tableColumnView/tableColumnView.interface"
import { type TableColumnViewTablesEnum } from "@finway-group/shared/lib/models/tableColumnView/tableColumnViewTables.enum"
import { useMutation, useQuery } from "@tanstack/react-query"
import i18n from "i18next"
import { queryClient } from "~Shared/config/reactQueryConfig"
import { useAuth } from "~Shared/hooks/auth.hooks"
import { NotificationService } from "~Shared/services"
import { NotificationDurationSec, NotificationTypeEnum } from "~Shared/services/notification.service"
import { TableColumnViewService } from "~Shared/services/tableColumnView.service"
import { CacheEnum, GENERAL_QUERY_STALE_TIME } from "./consts"

const getTableColumnViewQueryKey = (companyId: string | undefined, table: TableColumnViewTablesEnum) => [CacheEnum.TABLE_COLUMN_VIEWS, companyId, table]

const invalidateTableColumnView = (companyId: string | undefined, table: TableColumnViewTablesEnum) =>
    queryClient.invalidateQueries({ queryKey: getTableColumnViewQueryKey(companyId, table) })

export const removeTableColumnViews = () => queryClient.removeQueries({ queryKey: [CacheEnum.TABLE_COLUMN_VIEWS] })

export const useTableColumnViewByTableQuery = (table: TableColumnViewTablesEnum) => {
    const { companyId } = useAuth()
    return useQuery({
        queryKey: getTableColumnViewQueryKey(companyId, table),
        queryFn: ({ signal }) => TableColumnViewService.getView(table, { signal }),
        staleTime: GENERAL_QUERY_STALE_TIME,
        enabled: Boolean(companyId),
    })
}

export const useSaveTableColumnViewMutation = () => {
    const { companyId } = useAuth()
    return useMutation({
        mutationFn: ({ table, columns }: { table: TableColumnViewTablesEnum; columns: TableColumnView }) => TableColumnViewService.saveView(table, columns),
        onSuccess: async (columns, { table }) => {
            queryClient.setQueryData(getTableColumnViewQueryKey(companyId, table), columns)
            await invalidateTableColumnView(companyId, table)
            NotificationService.send(
                NotificationTypeEnum.SUCCESS,
                i18n.t("notification:table_column_view.saved.title"),
                i18n.t("notification:table_column_view.saved.message"),
                NotificationDurationSec.DEFAULT,
            )
        },
        onError: (error) => {
            NotificationService.showErrorNotificationBasedOnResponseError(error, i18n.t("error:table_column_view.save.title"))
        },
    })
}
